(function (root) {
  'use strict';
  if (root.__kmCartVariantSwitcherReady) return;

  function parseProperties(value) {
    if (!value) return {};
    try {
      var parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return Object.fromEntries(parsed.filter(function (entry) {
          return Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string';
        }));
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function route(form, path) {
    var rootUrl = (form && form.dataset.cartRoot) || '/';
    return rootUrl.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  async function request(url, body) {
    var response;
    try {
      var options = {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {Accept: 'application/json'}
      };
      if (body) {
        options.method = 'POST';
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
      response = await (root.KMCartState?.fetch || root.fetch.bind(root))(url, options);
    } catch (error) {
      throw new Error('Unable to update this product option. Please try again.');
    }
    var result;
    try { result = await response.json(); }
    catch (error) { throw new Error('Unable to read the cart update response. Please try again.'); }
    if (!response.ok || !result || result.status) {
      var responseError = new Error(result && (result.description || result.message) || 'Unable to update this product option. Please try again.');
      responseError.status = Number(result && result.status || response.status);
      throw responseError;
    }
    if (body && (!Array.isArray(result.items) || (Array.isArray(body.items) &&
      !body.items.every(function (requested) {
        return result.items.some(function (item) {
          return item && item.key && String(item.variant_id || item.id) === String(requested.id);
        });
      })))) {
      throw new Error('Unable to verify the cart update. Please refresh your cart and try again.');
    }
    return result;
  }

  async function readCart(form) {
    if (root.KMCartState) return root.KMCartState.read(route(form, 'cart.js'), {fresh: true});
    var cart = await request(route(form, 'cart.js'));
    if (!Array.isArray(cart.items)) throw new Error('Unable to read your cart. Please try again.');
    return cart;
  }

  function propertyIdentity(properties) {
    return JSON.stringify(Object.keys(properties || {}).sort().map(function (key) {
      return [key, properties[key]];
    }));
  }

  function sellingPlan(item) {
    return String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
  }

  function findLine(cart, reference) {
    var exact = reference.key && cart.items.find(function (item) { return item.key === reference.key; });
    if (exact) return exact;
    var matches = cart.items.filter(function (item) {
      return String(item.variant_id || item.id) === String(reference.variant_id || reference.id)
        && sellingPlan(item) === sellingPlan(reference)
        && propertyIdentity(item.properties) === propertyIdentity(reference.properties);
    });
    if (matches.length !== 1) throw new Error('Unable to identify this cart item. Please refresh your cart and try again.');
    return matches[0];
  }

  async function switchVariant(select) {
    if (!select || select.dataset.kmVariantPending === 'true') return;
    var form = select.closest('form');
    var previous = select.dataset.kmVariantCurrent || select.value;
    var next = select.value;
    if (!next || next === previous || select.dataset.kmVariantRefreshFailed === 'true') return;
    var key = select.dataset.kmCartVariantKey;
    var reference = {key: key, id: Number(previous), properties: parseProperties(select.dataset.kmCartVariantProperties),
      selling_plan: select.dataset.kmCartVariantSellingPlan};
    var errorNode = select.closest('[data-km-cart-line]')?.querySelector('[data-km-cart-variant-error]')
      || select.parentElement?.querySelector('[data-km-cart-variant-error]');
    var nativeCart = form && form.kmCartForm;
    const analyticsItem = root.KMCartAnalytics?.line(select);
    var operation = nativeCart && nativeCart.beginOperation && nativeCart.beginOperation('variant');
    var succeeded = false;
    var changed = false;
    select.dataset.kmVariantPending = 'true';
    select.disabled = true;
    if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
    try {
      var beforeAdd = await readCart(form);
      var source = findLine(beforeAdd, reference);
      var quantity = Math.max(1, Number(source.quantity));
      var item = {id: Number(next), quantity: quantity, properties: source.properties || {}};
      if (sellingPlan(source)) item.selling_plan = Number(sellingPlan(source));
      var matchesTarget = function (line) {
        return String(line.variant_id || line.id) === String(item.id)
          && sellingPlan(line) === sellingPlan(item)
          && propertyIdentity(line.properties) === propertyIdentity(item.properties);
      };
      var previousTargetQuantity = beforeAdd.items.filter(matchesTarget)
        .reduce(function (sum, line) { return sum + Number(line.quantity); }, 0);
      var added;
      try {
        added = await request(route(form, 'cart/add.js'), {items: [item]});
      } catch (addError) {
        // Do not replay an uncertain add: Shopify may already have committed it.
        // An explicit client rejection (including sold out) is never proof of success.
        if (addError.status >= 400 && addError.status < 500) throw addError;
        var verifiedTargets;
        try { verifiedTargets = (await readCart(form)).items.filter(matchesTarget); }
        catch (verificationError) { throw addError; }
        if (verifiedTargets.length !== 1
          || Number(verifiedTargets[0].quantity) < previousTargetQuantity + quantity) throw addError;
        added = {items: verifiedTargets};
      }
      try {
        var currentSource = findLine(await readCart(form), source);
        const finalCart = await request(route(form, 'cart/change.js'), {id: currentSource.key, quantity: 0});
        if (analyticsItem && !finalCart.items.some(row => row.key === currentSource.key)
          && finalCart.items.some(row => String(row.variant_id || row.id) === next)) {
          root.KMCartAnalytics?.trackProduct('change_cart_item_variant', analyticsItem);
        }
      } catch (error) {
        var addedLine = added.items && added.items[0];
        var removalVerified = false;
        try {
          if (!addedLine || !addedLine.key) throw new Error('Missing added cart line.');
          var latest = await readCart(form);
          var sourceRemains = latest.items.some(function (line) {
            return line.key === source.key || (String(line.variant_id || line.id) === String(source.variant_id || source.id)
              && sellingPlan(line) === sellingPlan(source)
              && propertyIdentity(line.properties) === propertyIdentity(source.properties));
          });
          var currentAdded = findLine(latest, {key: addedLine.key, id: item.id,
            properties: item.properties, selling_plan: item.selling_plan});
          // A lost response can follow a committed removal. Keep that replacement.
          removalVerified = !sourceRemains && Number(currentAdded.quantity) >= quantity;
          if (!removalVerified) {
            if (!sourceRemains) throw new Error('Unable to verify the replacement quantity.');
            // Shopify merges matching lines; undo only the quantity added by this switch.
            await request(route(form, 'cart/change.js'), {
              id: currentAdded.key,
              quantity: Math.max(0, Number(currentAdded.quantity) - quantity)
            });
          }
        } catch (rollbackError) {
          root.document.dispatchEvent(new root.CustomEvent('cart:build'));
          throw new Error(error.message + ' Please review your cart before checking out.');
        }
        if (!removalVerified) throw error;
      }
      select.dataset.kmVariantCurrent = next;
      changed = true;
      root.document.dispatchEvent(new root.CustomEvent('cart:build'));
      if (nativeCart && nativeCart.cartBuildPromise) await nativeCart.cartBuildPromise;
      succeeded = true;
    } catch (error) {
      if (nativeCart?.cartBuildRunning) {
        try { await nativeCart.cartBuildPromise; }
        catch (refreshError) {  }
      }
      if (!select.isConnected && form?.isConnected) {
        const candidates = [...form.querySelectorAll('[data-km-cart-variant-select]')];
        const matches = candidates.filter(candidate =>
          candidate.dataset.kmVariantCurrent === previous
          && propertyIdentity(parseProperties(candidate.dataset.kmCartVariantProperties)) === propertyIdentity(reference.properties)
          && String(candidate.dataset.kmCartVariantSellingPlan || '') === String(reference.selling_plan || ''));
        select = candidates.find(candidate => candidate.dataset.kmCartVariantKey === key)
          || (matches.length === 1 ? matches[0] : select);
        errorNode = select.closest('[data-km-cart-line]')?.querySelector('[data-km-cart-variant-error]');
      }
      if (!changed) select.value = previous;
      if (errorNode?.isConnected) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
      }
      nativeCart?.showCartRefreshError?.('variant');
    } finally {
      if (operation) nativeCart.finishOperation(operation, succeeded);
      delete select.dataset.kmVariantPending;
      var refreshFailed = changed && !succeeded;
      select.dataset.kmVariantRefreshFailed = String(refreshFailed);
      select.disabled = refreshFailed;
      if (refreshFailed && nativeCart && nativeCart.showCartRefreshError) nativeCart.showCartRefreshError('variant');
    }
  }

  function handleChange(event) {
    var select = event.target.closest && event.target.closest('[data-km-cart-variant-select]');
    if (select) switchVariant(select);
  }

  root.document.addEventListener('change', handleChange);
  root.__kmCartVariantSwitcherReady = true;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {parseProperties: parseProperties, switchVariant: switchVariant};
  }
})(typeof window !== 'undefined' ? window : globalThis);
